import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  JOIN_VENUE_EVENT,
  SPOT_CHANGE_EVENT,
  spotChangeSchema,
  type SpotChange,
} from '@kratispot/shared';
import { API_BASE } from '@/lib/api';

export const LIVE_STATES = ['connecting', 'live', 'offline'] as const;

export type LiveState = (typeof LIVE_STATES)[number];

export type VenueSocketHandlers = {
  readonly onChange: (change: SpotChange) => void;
  readonly onResync: () => void;
};

// A join that never answers means the link is dead, whatever the socket thinks.
export const JOIN_TIMEOUT_MS = 3000;

// Same origin unless the API lives elsewhere; the dev proxy carries the socket.
export function socketOrigin(base: string): string | undefined {
  return base === '' ? undefined : base;
}

// Handlers are read through a ref, so a re-render never drops the connection.
export function useVenueSocket(venueId: string, handlers: VenueSocketHandlers): LiveState {
  const [state, setState] = useState<LiveState>('connecting');
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    const socket: Socket = io(socketOrigin(API_BASE), {
      withCredentials: true,
      path: '/socket.io',
    });

    // Every join re-reads the venue, so a device that missed changes is whole. §16
    const join = (): void => {
      setState('connecting');
      socket
        .timeout(JOIN_TIMEOUT_MS)
        .emit(JOIN_VENUE_EVENT, { venueId }, (error: unknown, ack: { ok: boolean } | undefined) => {
          if (error === null && ack?.ok === true) {
            setState('live');
            latest.current.onResync();
            return;
          }
          setState('offline');
          // A link the device believes is up but nobody answers on: rebuild it.
          if (error !== null) socket.disconnect().connect();
        });
    };

    // The device's own switches answer at once; a socket only ever times out.
    const wentOffline = (): void => setState('offline');
    const cameBack = (): void => (socket.connected ? join() : void socket.connect());

    socket.on('connect', join);
    socket.on('disconnect', () => setState('offline'));
    socket.on('connect_error', () => setState('offline'));
    socket.on(SPOT_CHANGE_EVENT, (payload: unknown) => {
      const change = spotChangeSchema.safeParse(payload);
      if (change.success && change.data.venueId === venueId) latest.current.onChange(change.data);
    });
    window.addEventListener('offline', wentOffline);
    window.addEventListener('online', cameBack);

    return () => {
      window.removeEventListener('offline', wentOffline);
      window.removeEventListener('online', cameBack);
      socket.close();
    };
  }, [venueId]);

  return state;
}
